/**
 * Wires the connected wallet's order streams into the lifecycle tracker (#679). Mounted once in the Layout.
 *
 * Toasts are for the CONNECTED wallet only (also while ghosting someone else: your own orders still matter).
 * WS orderUpdates → orderEvents (deduped) → tracker; WS userFills → tracker (snapshot frames never toast, except the one
 * summing up a tab's return, see lifecycle.ts).
 */
import { useEffect } from 'react';
import { useOnTabWake, useSubscription } from '../../hl/react';
import { useWallet } from '../../wallet/context';
import { useOrderUpdates } from './hooks';
import { formatPx, labelCoin, readableLabel, useLabelIndex } from './labels';
import { orderLifecycle, type OrderLifecycle } from './lifecycle';
import { orderEvents } from './orderEvents';

function Wire({ user, tracker }: { user: string; tracker: OrderLifecycle }) {
  const index = useLabelIndex();
  useEffect(() => {
    tracker.fmt = {
      label: (coin) => readableLabel(coin, index),
      px: (px, coin) => formatPx(px, labelCoin(coin, index).kind),
    };
  }, [tracker, index]);

  useOrderUpdates(user);
  useOnTabWake((since) => tracker.expectAway(user, since));
  useEffect(
    () =>
      orderEvents.subscribe((e) => {
        if (e.user === user) tracker.onOrderUpdate(user, e.update.status, e.update.order);
      }),
    [user, tracker],
  );
  useSubscription({ type: 'userFills', user }, (msg) => {
    if (msg && Array.isArray(msg.fills)) tracker.onFills(user, msg.fills, msg.isSnapshot === true);
  });
  return null;
}

export default function OrderToasts({ tracker = orderLifecycle }: { tracker?: OrderLifecycle }) {
  const wallet = useWallet();
  const user = wallet.status === 'connected' && wallet.address ? wallet.address.toLowerCase() : null;
  return user ? <Wire key={user} user={user} tracker={tracker} /> : null;
}
